import { LightningElement, api } from 'lwc';

import { fetchAuthSession } from 'aws-amplify/auth';
import { get, patch, post } from 'aws-amplify/api';



const LANGUAGE_OPTIONS = [
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
];
const GENDER_OPTIONS = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'non_binary', label: 'Non-binary' },
    { value: 'prefer_not_to_say', label: 'Prefer not to say' },
];
const ACCOUNT_SKELETION = [
    {
        icon: 'utility:photo',
        label: 'Profile Photo',
        isFile: true,
        valueArr: [
            {
                label: 'Profile Photo',
                fieldApiName: 'profile_photo',
                value: '',
                displayValue: '',
                dtype: 'file'
            }
        ]
    },
    {
        icon: 'utility:slack_user',
        label: 'Name',
        isHorizontal: true,
        valueArr: [
            { label: 'First Name', fieldApiName: 'first_name', value: '', displayValue: '' },
            { label: 'Last Name', fieldApiName: 'last_name', value: '', displayValue: '' }
        ]
    },
    {
        icon: 'utility:profile',
        label: 'Gender',
        valueArr: [
            {
                label: 'Gender',
                fieldApiName: 'gender',
                value: '',
                displayValue: '',
                dtype: 'picklist',
                options: GENDER_OPTIONS
            }
        ]
    },
    {
        icon: 'utility:email',
        label: 'Email',
        valueArr: [
        { label: 'Email', fieldApiName: 'email', value: '', displayValue: '' }
        ]
    },
    {
        icon: 'utility:call',
        label: 'Phone',
        valueArr: [
        { label: 'Phone', fieldApiName: 'phone', value: '', displayValue: '' }
        ]
    },
    {
        icon: 'utility:key_dates',
        label: 'Birthday',
        valueArr: [
        {
            label: 'Birthday',
            fieldApiName: 'birthday',
            value: null,
            displayValue: '',
            min: new Date('1910-01-01'),
            max: new Date(),
            dtype: 'date'
        }
        ]
    },
    {
        icon: 'utility:http',
        label: 'Language',
        valueArr: [
        {
            label: 'Language',
            fieldApiName: 'language',
            value: '',
            displayValue: '',
            dtype: 'picklist',
            options: LANGUAGE_OPTIONS
        }
        ]
    },
    {
        icon: 'utility:home',
        label: 'Address',
        isaddress: true,
        valueArr: [
            { 
                label: 'Street', 
                fieldApiName: 'street', 
                value: '', 
                displayValue: '' 
            },
            { 
                label: 'City', 
                fieldApiName: 'city', 
                value: '', 
                displayValue: '' 
            },
            { 
                label: 'State', 
                fieldApiName: 'state', 
                value: '', 
                displayValue: '' 
            },
            { 
                label: 'Zip Code', 
                fieldApiName: 'zip_code', 
                value: '', 
                displayValue: '' 
            },
            { 
                label: 'County', 
                fieldApiName: 'county', 
                value: '', 
                displayValue: '' 
            },
            { 
                label: 'Country', 
                fieldApiName: 'country', 
                value: '', 
                displayValue: '' 
            },
        ]
    }
];

let KEY_ID = 930203;

import { IS_TESTING_NO_INTERNET } from '../../../utils/basicUtils';

export default class getWetGuestAccountDetails extends LightningElement {
    _accountInfo = [];
    _user = IS_TESTING_NO_INTERNET ? {id: '121212'} : null;
    _idToken;
    _isLoading = false;

    @api
    set mobile(value) {
        this._mobile = value;
    }
    get mobile() {
        return this._mobile;
    }


    async connectedCallback() {
        await this.loadMe();
    }
    async loadMe() {
        this._isLoading = true;
        try {
            if (IS_TESTING_NO_INTERNET) { 
                this._accountInfo = this.applyUserToSkeleton({});
                return;
            }
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();

            if (!idToken) {
                throw new Error('No idToken (not signed in)');
            }   
            this._idToken = idToken;

            const op = get({
                apiName: 'api',
                path: '/cognito/me',
                options: {
                    headers: {
                        Authorization: `Bearer ${idToken}`,
                    },
                },
            });

            const { body } = await op.response;
            const payload = await body.json();

            const user = payload?.user || payload;
            
            this._user = user;
            this._accountInfo = this.applyUserToSkeleton(user);
            this.dispatchProfileUpdated(user);

        } catch (error) {
            console.error('Error loading user profile', error);
        } finally {
            this._isLoading = false;
        }
    }

    
    applyUserToSkeleton(user) {
        try {
            return ACCOUNT_SKELETION.map(section => ({
                ...section,
                valueArr: section.valueArr.map(row => {
                    const rawValue = user?.[row.fieldApiName];

                    let value = rawValue ?? '';
                    let displayValue = rawValue ?? '';

                    // Date handling
                    if (row.dtype === 'date' && rawValue) {
                        const isoDate = String(rawValue).split('T')[0]; // 1991-05-26

                        value = isoDate;
                        const parts = isoDate.split('-');
                        displayValue = `${parts[1]}/${parts[2]}/${parts[0]}`;
                    }

                    // Picklist handling
                    if (row.dtype === 'picklist' && rawValue) {
                        const match = row.options?.find(o => o.value === rawValue);
                        displayValue = match ? match.label : rawValue;
                    }

                    return {
                        ...row,
                        value,
                        displayValue
                    };
                })?.filter(Boolean)
            }));
        } catch(error) {
            console.error('Error in applyUserToSkeleton() of getWetGuestAccountDetails.js', error);
        }
    }
    async updateUserFields(message) {
        try {
            const fields = message.detail;
            if (!fields || !Object.keys(fields).length) return;

            // normalize birthday before send
            if (fields.birthday instanceof Date) {
                fields.birthday = fields.birthday.toISOString().slice(0, 10);
            }

            const op = patch({
                apiName: 'api',
                path: '/cognito/me',
                options: {
                    headers: {
                        Authorization: `Bearer ${this._idToken}`, // keep, since you’re using User Pool authorizer here
                        'Content-Type': 'application/json'
                    },
                    body: fields
                }
            });


            const { body } = await op.response;
            const payload = await body.json();

            const user = payload?.user || payload;
            this._user = user;
            this._accountInfo = this.applyUserToSkeleton(user);
            this.dispatchProfileUpdated(user);

            return user;

        } catch (error) {
            console.error('Error updating profile', error);
            throw error;
        }
    }


    modalConfig;    
    handleProfilePhoto() {
        this.modalConfig = {
            value: [
                {   
                    url: this._user?.profile_photo_url,
                    name: this._user?.profile_photo_key?.split('/').pop(),
                    s3_key: this._user?.profile_photo_key, 
                    keyId: String(KEY_ID++)
                }
            ]
        }
    }
    closeModal() {
        this.modalConfig = null;
    }


    // ==================== Handle Save Profile Photo ==================== \\
    // S3 bucket Image upload trial
    _filesLoading = false;
    _selectedPhoto = {};

    async savePhoto() {
        this._filesLoading = true;
        try {
            if (this._selectedPhoto?.file) {
                const profilePhotoResult = await this.uploadAndSaveProfilePhoto(this._selectedPhoto.file);

                this._user = {
                    ...this._user,
                    ...profilePhotoResult.app_user,
                    profile_photo_key: profilePhotoResult?.app_user?.profile_photo_key ?? this._user?.profile_photo_key,
                    profile_photo_url: this._selectedPhoto?.newPhoto?.url ?? this._user?.profile_photo_url
                };
                this.dispatchProfileUpdated(this._user);
                this.showToast('Profile photo updated.', '', 'success');

            } else {
                console.log('No new file found in event.detail:', event.detail);
            }
        } catch(error) {
            this.showToast('Error in savePhoto() of getWetGuestAccountDetails.js', error, 'error');
        } finally {
            this._filesLoading = false;
        }
    }

    
    async handleFileSelected(event) {
        this._filesLoading = true;
        try {
            const files = Array.isArray(event.detail) ? event.detail : [];
            const newPhoto = files.find(f => f?.file instanceof File);
            const file = newPhoto?.file ?? null;
            this._selectedPhoto = {
                file,
                newPhoto
            };
            if (IS_TESTING_NO_INTERNET) {
                return;
            }
            return;
            if (file) {
                const profilePhotoResult = await this.uploadAndSaveProfilePhoto(file);

                this._user = {
                    ...this._user,
                    ...profilePhotoResult.app_user,
                    profile_photo_key: profilePhotoResult?.app_user?.profile_photo_key ?? this._user?.profile_photo_key,
                    profile_photo_url: newPhoto?.url ?? this._user?.profile_photo_url
                };
                this.dispatchProfileUpdated(this._user);
                this.showToast('Profile photo updated.', '', 'success');

            } else {
                console.log('No new file found in event.detail:', event.detail);
            }
        } catch (error) {
            console.error('Error in handleFileSelected() of getWetGuestAccountDetails.js', error);
        } finally {
            this._filesLoading = false;
        }
    }
    // 🆕 replace uploadFileToS3() 👇 call with this
    async uploadAndSaveProfilePhoto(file) {
        const s3Key = await this.uploadFileToS3(file);
        const result = await this.saveProfilePhoto(s3Key);
        return result;
    }
    // Get the s3 key
    async uploadFileToS3(file) {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        if (!idToken) {
            throw new Error('No idToken available');
        }

        const op = post({
            apiName: 'api',
            path: '/cognito/uploads/presign',
            options: {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: {
                    fileName: file.name,
                    contentType: file.type || 'application/octet-stream'
                }
            }
        });

        const { body } = await op.response;
        const payload = await body.json();
        //console.log('presign payload:', payload);

        const uploadRes = await fetch(payload.uploadUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': file.type || 'application/octet-stream'
            },
            body: file
        });

        if (!uploadRes.ok) {
            const errText = await uploadRes.text();
            console.error('S3 upload failed body:', errText);
            throw new Error(`S3 upload failed: ${uploadRes.status}`);
        }

        return payload.key;
    }
    // 🆕 Save the photo to the DB
    async saveProfilePhoto(s3Key) {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString();

        if (!idToken) {
            throw new Error('No idToken available');
        }

        const op = post({
            apiName: 'api',
            path: '/cognito/profile-photo',
            options: {
                headers: {
                    Authorization: `Bearer ${idToken}`,
                    'Content-Type': 'application/json'
                },
                body: {
                    s3Key
                }
            }
        });

        const { body } = await op.response;
        return await body.json();
    }

    dispatchProfileUpdated(user) {
        this.dispatchEvent(new CustomEvent('profileupdated', {
            detail: { user },
            bubbles: true,
            composed: true
        }));
    }
    // ===================================================================== \\

    toastObj;
    showToast(title, message, variant, mode=null) {
        const modeP = mode 
            ? mode 
                : variant === 'success'
            ? 'dismissible'
            : 'sticky';
        
        this.toastObj = { 
            variant: variant, 
            mode: modeP, 
            message: message,
            header: { 
                text: title
            }        
        };
    }
}
